import { useEffect, useMemo, useState } from "react";
import { getCurrentSession } from "../../services/auth";

const API_URL = (
  process.env.REACT_APP_API_URL || "https://zedro-backend.onrender.com"
).replace(/\/$/, "");

const GROUPS = [
  {
    key: "systems",
    title: "Sistemas / experiencias",
    description: "Contenido formativo creado en Producción.",
  },
  {
    key: "tools",
    title: "Herramientas",
    description: "Calculadoras, plantillas, automatizaciones y herramientas aplicables.",
  },
  {
    key: "assets",
    title: "Recursos",
    description: "PDF, audio, workbook y demás activos descargables o consultables.",
  },
  {
    key: "editorials",
    title: "Editoriales",
    description: "Contenido editorial que forme parte explícita de la oferta.",
  },
  {
    key: "ai_flows",
    title: "Experiencias con IA",
    description: "Flujos de IA habilitados específicamente para este producto.",
  },
];

function authHeaders(includeJson = false) {
  const session = getCurrentSession();
  const headers = {};
  if (includeJson) headers["Content-Type"] = "application/json";
  if (session?.token) headers.Authorization = `Bearer ${session.token}`;
  return headers;
}

function snapshotFromAssociations(associations = {}) {
  return Object.fromEntries(
    GROUPS.map(({ key }) => [
      key,
      (associations[key] || [])
        .filter((item) => item.linked)
        .map((item) => item.id)
        .sort(),
    ])
  );
}

function normalizedSnapshot(snapshot = {}) {
  return Object.fromEntries(
    GROUPS.map(({ key }) => [key, [...(snapshot[key] || [])].sort()])
  );
}

export default function ProductAssociationManager({ productId }) {
  const [associations, setAssociations] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(
          `${API_URL}/api/admin/product-associations/${productId}`,
          { headers: authHeaders() }
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data?.associations) {
          throw new Error(
            data?.message || data?.error || "No fue posible cargar la composición."
          );
        }

        const snapshot = snapshotFromAssociations(data.associations);
        if (alive) {
          setAssociations(data.associations);
          setBaseline(snapshot);
          setSelected(snapshot);
        }
      } catch (err) {
        console.error("[ADMIN_PRODUCT_ASSOCIATIONS_LOAD]", err);
        if (alive) {
          setError(err.message || "No fue posible cargar la composición.");
        }
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [productId]);

  const dirty = useMemo(() => {
    if (!baseline || !selected) return false;
    return (
      JSON.stringify(normalizedSnapshot(baseline)) !==
      JSON.stringify(normalizedSnapshot(selected))
    );
  }, [baseline, selected]);

  const selectedCount = useMemo(() => {
    if (!selected) return 0;
    return GROUPS.reduce((total, { key }) => total + selected[key].length, 0);
  }, [selected]);

  function toggle(key, itemId) {
    setNotice("");
    setError("");
    setSelected((current) => {
      const active = new Set(current[key]);
      if (active.has(itemId)) active.delete(itemId);
      else active.add(itemId);
      return { ...current, [key]: [...active] };
    });
  }

  function discard() {
    if (!baseline) return;
    setSelected(normalizedSnapshot(baseline));
    setNotice("Cambios descartados.");
    setError("");
  }

  async function save() {
    if (!selected || !dirty) return;
    setSaving(true);
    setError("");
    setNotice("");

    try {
      const payload = normalizedSnapshot(selected);
      const response = await fetch(
        `${API_URL}/api/admin/product-associations/${productId}`,
        {
          method: "PUT",
          headers: authHeaders(true),
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.associations) {
        throw new Error(
          data?.message || data?.error || "No fue posible guardar la composición."
        );
      }

      const snapshot = snapshotFromAssociations(data.associations);
      setAssociations(data.associations);
      setBaseline(snapshot);
      setSelected(snapshot);
      setNotice("Composición guardada correctamente.");
    } catch (err) {
      console.error("[ADMIN_PRODUCT_ASSOCIATIONS_SAVE]", err);
      setError(err.message || "No fue posible guardar la composición.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <section style={sectionStyle}>Cargando composición del producto...</section>;
  }

  if (!associations || !selected) {
    return (
      <section style={sectionStyle}>
        <h3 style={titleStyle}>Composición del producto</h3>
        <div style={errorStyle}>{error || "No fue posible cargar la composición."}</div>
      </section>
    );
  }

  const normalizedSearch = search.trim().toLowerCase();

  return (
    <section style={sectionStyle}>
      <div style={headerStyle}>
        <div>
          <div style={eyebrowStyle}>Contenido incluido</div>
          <h3 style={titleStyle}>Composición del producto</h3>
          <p style={descriptionStyle}>
            Selecciona las piezas creadas en Producción que formarán parte de este
            producto. Guardar reemplaza la composición completa en una sola operación.
          </p>
        </div>
        <div style={summaryStyle}>
          <strong>{selectedCount}</strong>
          <span>{selectedCount === 1 ? "pieza incluida" : "piezas incluidas"}</span>
        </div>
      </div>

      {error && <div style={errorStyle}>{error}</div>}
      {notice && <div style={noticeStyle}>{notice}</div>}

      <div style={toolbarStyle}>
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar contenido por nombre..."
          style={searchStyle}
        />
        {dirty && <span style={pendingStyle}>Cambios sin guardar</span>}
      </div>

      <div style={groupsStyle}>
        {GROUPS.map((group) => {
          const items = (associations[group.key] || []).filter((item) =>
            normalizedSearch
              ? String(item.label || "").toLowerCase().includes(normalizedSearch)
              : true
          );
          const selectedInGroup = selected[group.key].length;

          return (
            <div key={group.key} style={groupStyle}>
              <div style={groupHeaderStyle}>
                <div>
                  <h4 style={groupTitleStyle}>{group.title}</h4>
                  <p style={groupDescriptionStyle}>{group.description}</p>
                </div>
                <span style={countBadgeStyle}>{selectedInGroup} incluidos</span>
              </div>

              {items.length === 0 ? (
                <div style={emptyStyle}>
                  {normalizedSearch
                    ? "No hay coincidencias en esta categoría."
                    : "Todavía no hay contenido disponible en esta categoría."}
                </div>
              ) : (
                <div style={itemListStyle}>
                  {items.map((item) => {
                    const checked = selected[group.key].includes(item.id);
                    return (
                      <label key={item.id} style={itemStyle}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(group.key, item.id)}
                        />
                        <span style={itemTextStyle}>
                          <strong>{item.label}</strong>
                          <span style={metaStyle}>{item.status || "sin estado"}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={footerStyle}>
        <p style={helperStyle}>
          Esta selección define qué recibe el comprador de este producto. No duplica
          el contenido original de Producción.
        </p>
        <div style={actionsStyle}>
          <button
            type="button"
            onClick={discard}
            disabled={!dirty || saving}
            style={secondaryButtonStyle}
          >
            Descartar cambios
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            style={primaryButtonStyle}
          >
            {saving ? "Guardando..." : "Guardar composición"}
          </button>
        </div>
      </div>
    </section>
  );
}

const sectionStyle = {
  border: "1px solid #e6ebf1",
  borderRadius: 16,
  padding: 18,
  background: "#fff",
  boxShadow: "0 10px 30px rgba(0,0,0,.05)",
  marginBottom: 20,
};
const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 18,
  alignItems: "flex-start",
  flexWrap: "wrap",
};
const eyebrowStyle = {
  fontSize: 11,
  letterSpacing: ".1em",
  textTransform: "uppercase",
  color: "#9a7634",
  fontWeight: 900,
  marginBottom: 5,
};
const titleStyle = { margin: 0, color: "#0a2230", fontSize: 20 };
const descriptionStyle = {
  margin: "6px 0 0",
  maxWidth: 690,
  color: "#6b7a88",
  lineHeight: 1.55,
  fontSize: 13,
};
const summaryStyle = {
  minWidth: 120,
  padding: "10px 12px",
  borderRadius: 12,
  background: "#f4f7fa",
  color: "#264763",
  display: "grid",
  gap: 2,
  textAlign: "center",
  fontSize: 12,
};
const toolbarStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
  marginTop: 16,
};
const searchStyle = {
  flex: "1 1 280px",
  minHeight: 40,
  border: "1px solid #d7dee8",
  borderRadius: 9,
  padding: "8px 10px",
  font: "inherit",
};
const pendingStyle = {
  padding: "6px 9px",
  borderRadius: 999,
  background: "#fff7e6",
  color: "#8a621f",
  fontSize: 12,
  fontWeight: 800,
};
const groupsStyle = { display: "grid", gap: 14, marginTop: 16 };
const groupStyle = {
  border: "1px solid #e6ebf1",
  borderRadius: 13,
  padding: 14,
};
const groupHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  alignItems: "flex-start",
  flexWrap: "wrap",
};
const groupTitleStyle = { margin: 0, color: "#213348", fontSize: 15 };
const groupDescriptionStyle = {
  margin: "4px 0 0",
  color: "#7b8797",
  fontSize: 12,
};
const countBadgeStyle = {
  padding: "5px 8px",
  borderRadius: 999,
  background: "#eef3f7",
  color: "#264763",
  fontSize: 11,
  fontWeight: 800,
};
const itemListStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: 8,
  marginTop: 12,
};
const itemStyle = {
  display: "flex",
  alignItems: "flex-start",
  gap: 9,
  padding: 10,
  border: "1px solid #eef2f7",
  borderRadius: 10,
  cursor: "pointer",
};
const itemTextStyle = { display: "grid", gap: 3, color: "#2c3b51", fontSize: 13 };
const metaStyle = { color: "#8a96a3", fontSize: 11, textTransform: "uppercase" };
const emptyStyle = {
  marginTop: 10,
  padding: 10,
  borderRadius: 9,
  background: "#f8fafc",
  color: "#7b8797",
  fontSize: 12,
};
const footerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 14,
  flexWrap: "wrap",
  marginTop: 18,
  paddingTop: 14,
  borderTop: "1px solid #eef2f7",
};
const helperStyle = { margin: 0, color: "#7b8797", fontSize: 12, maxWidth: 600 };
const actionsStyle = { display: "flex", gap: 9, flexWrap: "wrap" };
const primaryButtonStyle = {
  minHeight: 40,
  padding: "0 15px",
  border: 0,
  borderRadius: 10,
  background: "#264763",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
};
const secondaryButtonStyle = {
  minHeight: 40,
  padding: "0 15px",
  border: "1px solid #cfd8e3",
  borderRadius: 10,
  background: "#fff",
  color: "#405064",
  fontWeight: 800,
  cursor: "pointer",
};
const errorStyle = {
  marginTop: 14,
  padding: "10px 12px",
  borderRadius: 9,
  background: "#fef2f2",
  border: "1px solid #fecaca",
  color: "#b42318",
  fontSize: 12,
};
const noticeStyle = {
  marginTop: 14,
  padding: "10px 12px",
  borderRadius: 9,
  background: "#ecfdf3",
  border: "1px solid #abefc6",
  color: "#067647",
  fontSize: 12,
};
